import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardActionArea,
    Typography,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    Pagination,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    useTheme,
    useMediaQuery,
    Divider,
    Stack,
    Avatar,
    Badge,
} from '@mui/material';
import {
    Work,
    WorkOff,
    AccessTime,
    Person,
    CalendarToday,
    Schedule,
    Refresh,
    ChevronRight,
    Edit,
} from '@mui/icons-material';
import rosterService, { Shift } from '../../services/rosterService';
import staffService from '../../services/staffService';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '@/components/Layout/PageLayout';
import { formatDateTimeForDisplay } from '../../utils/dateUtils';
import ShiftDetailsModal from '../../components/Roster/ShiftDetailsModal';
import { enrichShiftsWithNames } from '../../utils/nameUtils';
import { captureCurrentLocation } from '../../utils/geolocation';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const UserShiftsPage: React.FC = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
    const [currentStaffProfile, setCurrentStaffProfile] = useState<any>(null);
    const [allShifts, setAllShifts] = useState<Shift[]>([]);
    const [upcomingShifts, setUpcomingShifts] = useState<Shift[]>([]);
    const [historyShifts, setHistoryShifts] = useState<Shift[]>([]);
    const [todayShifts, setTodayShifts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    const [shiftDetailsModalOpen, setShiftDetailsModalOpen] = useState(false);
    const [selectedShiftDetails, setSelectedShiftDetails] = useState<Shift | null>(null);

    const [clients, setClients] = useState<any[]>([]);
    const [staff, setStaff] = useState<any[]>([]);

    const [clockDialog, setClockDialog] = useState<{
        open: boolean;
        type: 'in' | 'out';
        shift?: any;
    }>({ open: false, type: 'in' });
    const [notes, setNotes] = useState('');
    const [travelKm, setTravelKm] = useState('');
    const [clockOutWorkInfo, setClockOutWorkInfo] = useState('');
    const [clockDialogError, setClockDialogError] = useState<string | null>(null);

    // Edit Shift Info dialog state
    const [editShiftDialog, setEditShiftDialog] = useState<{ open: boolean; shift?: any }>({ open: false });
    const [editWorkInfo, setEditWorkInfo] = useState('');
    const [editTravelKm, setEditTravelKm] = useState('');
    const [editDialogError, setEditDialogError] = useState<string | null>(null);
    const [editDialogLoading, setEditDialogLoading] = useState(false);

    useEffect(() => {
        if (user) loadCurrentStaffProfile();
    }, [user]);

    useEffect(() => {
        if (selectedStaffId) loadShifts();
    }, [selectedStaffId, page]);

    const loadCurrentStaffProfile = async () => {
        if (!user?.id) return;
        try {
            const [staffProfile, clientsData, staffData] = await Promise.all([
                staffService.getStaffByUserId(user.id),
                rosterService.getClientsDropdown(),
                rosterService.getStaffDropdown(),
            ]);
            setClients(clientsData);
            setStaff(staffData);
            if (staffProfile) {
                setCurrentStaffProfile(staffProfile);
                setSelectedStaffId(staffProfile.id);
            } else {
                setError('Staff profile not found. Please contact your administrator.');
            }
        } catch (err: any) {
            setError('Failed to load staff profile');
        }
    };

    const loadShifts = async () => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * limit;
            const [allResponse, upcomingResponse, historyResponse, todayResponse] = await Promise.all([
                rosterService.getStaffShifts(selectedStaffId, limit, offset),
                rosterService.getUpcomingStaffShifts(selectedStaffId, 7),
                rosterService.getStaffShiftHistory(selectedStaffId, 30),
                rosterService.getStaffTodayShifts(selectedStaffId),
            ]);

            const simplify = (shifts: any[]) =>
                shifts.map((s) => ({
                    ...s,
                    clientName: s.clientName ? s.clientName.split(' • ')[0] : s.clientName,
                    staffName: s.staffName ? s.staffName.split(' • ')[0] : s.staffName,
                }));

            const allEnriched = simplify(enrichShiftsWithNames(allResponse.data || [], staff, clients));
            const upcomingEnriched = simplify(enrichShiftsWithNames(upcomingResponse.data || [], staff, clients));
            setAllShifts(allEnriched);
            setHistoryShifts(simplify(enrichShiftsWithNames(historyResponse.data || [], staff, clients)));

            // Backend's /shifts/today endpoint filters by SERVER UTC date,
            // which can drop a shift on the user's local "today" when the
            // user is in a timezone ahead of UTC (e.g. Australia). Derive
            // today / upcoming on the client using the user's local date so
            // a shift that's on today's local calendar shows under "Today".
            const todayLocal = new Date();
            todayLocal.setHours(0, 0, 0, 0);
            const tomorrowLocal = new Date(todayLocal);
            tomorrowLocal.setDate(tomorrowLocal.getDate() + 1);

            // Backend serialises DateTimes without a Z marker but they're UTC
            // values. `new Date()` would mis-parse those as local. Force UTC
            // parsing so the local-day comparison is correct.
            const parseUtc = (iso?: string) => {
                if (!iso) return null;
                const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(iso);
                return new Date(hasTz ? iso : `${iso}Z`);
            };
            const isOnLocalDay = (iso?: string) => {
                const d = parseUtc(iso);
                if (!d || isNaN(d.getTime())) return false;
                return d >= todayLocal && d < tomorrowLocal;
            };

            // Convert TimeClockStatus rows from /shifts/today (used for the
            // clock-in/out controls) so they're enrichable like the others.
            const enrichedBackendToday = (todayResponse.data || []).map((shift: any) => {
                let actualClientId = shift.clientId;
                if (!actualClientId && shift.clientName) {
                    const match = shift.clientName.match(/Client (\d+)/);
                    if (match) actualClientId = parseInt(match[1]);
                }
                const clientInfo = clients.find((c) => c.value == actualClientId || c.id == actualClientId);
                const staffInfo = staff.find((s) => s.value == shift.staffId || s.id == shift.staffId);
                // Name resolution priority:
                //   1. Local clients-dropdown lookup (always real names)
                //   2. Backend-provided clientName (real name when the
                //      Roster service has the client cached, otherwise a
                //      "Client {id}" placeholder — still better than
                //      "Unknown Client" which gives the staff zero context)
                //   3. Synthetic placeholder from the resolved id
                //   4. Last-resort "Unknown Client"
                const isPlaceholder = (n?: string) =>
                    !n || /^client\s*\d+$/i.test(n) || /^unknown/i.test(n);
                const resolvedClient =
                    clientInfo?.name
                    || (!isPlaceholder(shift.clientName) ? shift.clientName : null)
                    || (actualClientId ? `Client #${actualClientId}` : null)
                    || 'Unknown Client';
                const resolvedStaff =
                    staffInfo?.name
                    || (!isPlaceholder(shift.staffName) ? shift.staffName : null)
                    || (shift.staffId ? `Staff #${shift.staffId}` : null)
                    || 'Unknown Staff';
                return {
                    ...shift,
                    clientId: actualClientId,
                    clientName: resolvedClient,
                    staffName: resolvedStaff,
                };
            });

            // Merge: anything from "all" that lands on the user's local date,
            // plus anything the backend's today endpoint returned (covers
            // active shifts that started yesterday and span midnight).
            const localTodayFromAll = upcomingEnriched.filter(s => isOnLocalDay(s.startDateTime));
            const mergedToday = [...enrichedBackendToday];
            for (const s of localTodayFromAll) {
                const sid = (s as any).id ?? (s as any).shiftId;
                if (!mergedToday.some(t => (t.shiftId ?? t.id) === sid)) {
                    mergedToday.push({
                        ...s,
                        shiftId: (s as any).id ?? (s as any).shiftId,
                        scheduledStart: s.startDateTime,
                        scheduledEnd: s.endDateTime,
                    });
                }
            }
            setTodayShifts(mergedToday);

            // Upcoming = next 7 days but EXCLUDING today's local shifts (they
            // already live in the Today tab).
            setUpcomingShifts(upcomingEnriched.filter(s => !isOnLocalDay(s.startDateTime)));
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load shifts');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString: string) => {
        try {
            return formatDateTimeForDisplay(dateString, 'MMM DD, YYYY HH:mm');
        } catch {
            return dateString;
        }
    };

    const formatDateShort = (dateString: string) => {
        try {
            return formatDateTimeForDisplay(dateString, 'MMM DD, HH:mm');
        } catch {
            return dateString;
        }
    };

    const getStatusColor = (status: string): 'primary' | 'success' | 'warning' | 'error' | 'default' => {
        switch (status?.toLowerCase()) {
            case 'scheduled': return 'primary';
            case 'confirmed': return 'success';
            case 'in_progress': return 'warning';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const handleClockAction = (type: 'in' | 'out', shift: any) => {
        setClockDialog({ open: true, type, shift });
        setNotes('');
        setTravelKm('');
        setClockOutWorkInfo('');
        setClockDialogError(null);
    };

    const performClockAction = async () => {
        setClockDialogError(null);
        try {
            const { type, shift } = clockDialog;
            if (!selectedStaffId) { setClockDialogError('Staff ID not found'); return; }

            // Pre-check: clock-in must be within 2 hours of scheduled start.
            // The backend enforces this too, but its error message is built
            // from raw UTC, which is confusing for staff. Doing the check here
            // lets us show the local-time message instead.
            if (type === 'in' && shift.scheduledStart) {
                const now = Date.now();
                const start = new Date(shift.scheduledStart).getTime();
                const earliest = start - 2 * 60 * 60 * 1000;
                if (now < earliest) {
                    setClockDialogError(
                        `You can only clock in up to 2 hours before your scheduled start time of ${formatDateTime(shift.scheduledStart)}.`
                    );
                    return;
                }
            }

            // Best-effort GPS fix — null if denied / unavailable. We still
            // record the clock action; admin will just see no location for it.
            const location = await captureCurrentLocation();
            const locArg = location
                ? { latitude: location.latitude, longitude: location.longitude, accuracyMetres: location.accuracyMetres }
                : undefined;

            if (type === 'in') {
                await rosterService.clockIn(shift.shiftId, selectedStaffId, notes.trim() || undefined, locArg);
                setSuccess(location ? 'Clocked in (location recorded).' : 'Clocked in (no location available).');
            } else {
                let kmParsed: number | undefined;
                if (travelKm.trim() !== '') {
                    const n = Number(travelKm);
                    if (!Number.isFinite(n) || n < 0) {
                        setClockDialogError('Travel kilometres must be a non-negative number.');
                        return;
                    }
                    kmParsed = Math.round(n * 100) / 100;
                }
                await rosterService.clockOut(shift.shiftId, selectedStaffId, notes.trim() || undefined, kmParsed, clockOutWorkInfo.trim() || undefined, locArg);
                setSuccess(location ? 'Clocked out (location recorded).' : 'Clocked out (no location available).');
            }
            await loadShifts();
            setClockDialog({ open: false, type: 'in' });
            setClockOutWorkInfo('');
            setClockDialogError(null);
        } catch (err: any) {
            // Show the failure inline in the dialog so the user sees it next
            // to the action they just took. The dialog stays open so they
            // can correct timing or close it themselves.
            let message: string = err?.response?.data?.message
                ?? err?.response?.data?.error
                ?? err?.message
                ?? 'Failed to perform clock action.';

            // Backend builds its 2-hour error from raw UTC, so the time it
            // shows is wrong for the user. Rewrite it with the shift's local
            // start time we already have on the dialog.
            const shift = clockDialog.shift;
            if (shift?.scheduledStart && /clock in up to 2 hours before/i.test(message)) {
                message = `You can only clock in up to 2 hours before your scheduled start time of ${formatDateTime(shift.scheduledStart)}.`;
            }
            setClockDialogError(message);
        }
    };

    const canClockIn = (shift: any) => {
        const status = shift.status?.toLowerCase();
        return (status === 'scheduled' || status === 'confirmed') && !shift.actualStart;
    };

    const canClockOut = (shift: any) => shift.actualStart && !shift.actualEnd;

    /** Returns true if the shift is clocked out and within 7 days of clock-out. */
    const canEditShiftInfo = (shift: any): boolean => {
        const endTime = shift.actualEnd ?? shift.actualEndTime;
        if (!endTime) return false;
        const endDate = new Date(endTime.endsWith('Z') ? endTime : endTime + 'Z');
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return endDate > sevenDaysAgo;
    };

    // Build clientDetails / staffDetails by joining the shift to the already-
    // loaded dropdown arrays, since the Roster backend doesn't populate them.
    const buildDetails = (clientId: number, staffId: number) => {
        const clientLookup = clients.find((c: any) => c.id == clientId || c.value == clientId);
        const staffLookup = staff.find((s: any) => s.id == staffId || s.value == staffId);
        return {
            clientDetails: clientLookup
                ? {
                    id: clientLookup.id,
                    name: clientLookup.name ?? '',
                    address: clientLookup.address ?? '',
                }
                : undefined,
            staffDetails: staffLookup
                ? {
                    id: staffLookup.id,
                    name: staffLookup.name ?? '',
                    employeeId: staffLookup.employeeId ?? '',
                    position: staffLookup.position ?? '',
                }
                : undefined,
        };
    };

    const handleOpenShiftDetails = async (shift: Shift | any) => {
        if (shift.shiftId && !shift.id) {
            try {
                const allShiftsResponse = await rosterService.getStaffShifts(selectedStaffId, 100, 0);
                const fullShiftData = allShiftsResponse.data?.find((s: any) => s.id === shift.shiftId);
                if (fullShiftData) {
                    const enriched = enrichShiftsWithNames([fullShiftData], staff, clients)[0];
                    const details = buildDetails(enriched.clientId, enriched.staffId);
                    setSelectedShiftDetails({
                        ...enriched,
                        clientName: enriched.clientName?.split(' • ')[0],
                        staffName: enriched.staffName?.split(' • ')[0],
                        ...details,
                    });
                } else {
                    const details = buildDetails(shift.clientId || 0, shift.staffId || 0);
                    setSelectedShiftDetails({
                        id: shift.shiftId, clientId: shift.clientId || 0, staffId: shift.staffId || 0,
                        clientName: shift.clientName || 'Unknown Client', staffName: shift.staffName || 'Unknown Staff',
                        shiftType: 'regular' as const, startDateTime: shift.scheduledStart,
                        endDateTime: shift.scheduledEnd, durationHours: shift.scheduledHours || 0,
                        status: shift.status || 'scheduled' as const,
                        actualStartTime: shift.actualStart, actualEndTime: shift.actualEnd,
                        hourlyRate: 0, totalAmount: 0,
                        ...details,
                    });
                }
            } catch {
                const details = buildDetails(shift.clientId || 0, shift.staffId || 0);
                setSelectedShiftDetails({
                    id: shift.shiftId, clientId: shift.clientId || 0, staffId: shift.staffId || 0,
                    clientName: shift.clientName || 'Unknown Client', staffName: shift.staffName || 'Unknown Staff',
                    shiftType: 'regular' as const, startDateTime: shift.scheduledStart,
                    endDateTime: shift.scheduledEnd, durationHours: shift.scheduledHours || 0,
                    status: shift.status || 'scheduled' as const,
                    actualStartTime: shift.actualStart, actualEndTime: shift.actualEnd,
                    hourlyRate: 0, totalAmount: 0,
                    ...details,
                });
            }
        } else {
            // Shift already has full data — but Roster never sets clientDetails,
            // so layer in the dropdown lookup if it isn't already there.
            const existing: any = shift;
            const details = buildDetails(existing.clientId, existing.staffId);
            setSelectedShiftDetails({
                ...existing,
                clientDetails: existing.clientDetails ?? details.clientDetails,
                staffDetails: existing.staffDetails ?? details.staffDetails,
            });
        }
        setShiftDetailsModalOpen(true);
    };

    const handleCloseShiftDetails = () => {
        setShiftDetailsModalOpen(false);
        setSelectedShiftDetails(null);
    };

    const handleOpenEditDialog = async (shift: any) => {
        // TimeClockStatus objects (today's shifts) use shiftId instead of id and
        // don't carry workInformation / travelKilometres. Look up the full Shift
        // record from the already-loaded arrays, or fetch from the API if missing.
        const shiftId = shift.id ?? shift.shiftId;
        let fullShift: any = shift;

        if (shift.shiftId && !shift.id) {
            const cached =
                allShifts.find((s) => s.id === shiftId) ??
                historyShifts.find((s) => s.id === shiftId);

            if (cached) {
                fullShift = cached;
            } else if (selectedStaffId) {
                try {
                    const resp = await rosterService.getStaffShifts(selectedStaffId, 100, 0);
                    fullShift = resp.data.find((s: any) => s.id === shiftId) ?? shift;
                } catch {
                    // fall back to the original shift object
                }
            }
        }

        setEditShiftDialog({ open: true, shift: fullShift });
        setEditWorkInfo(fullShift.workInformation ?? '');
        setEditTravelKm(
            typeof fullShift.travelKilometres === 'number' ? String(fullShift.travelKilometres) : '',
        );
        setEditDialogError(null);
    };

    const performEditShiftInfo = async () => {
        if (!selectedStaffId || !editShiftDialog.shift) return;
        const shiftId = editShiftDialog.shift.id ?? editShiftDialog.shift.shiftId;
        setEditDialogLoading(true);
        setEditDialogError(null);
        try {
            let kmParsed: number | undefined;
            if (editTravelKm.trim() !== '') {
                const n = Number(editTravelKm);
                if (!Number.isFinite(n) || n < 0) {
                    setEditDialogError('Travel kilometres must be a non-negative number.');
                    return;
                }
                kmParsed = Math.round(n * 100) / 100;
            }
            await rosterService.staffUpdateShift(
                shiftId,
                selectedStaffId,
                editWorkInfo.trim() || null,
                kmParsed ?? null,
            );
            setSuccess('Shift information updated successfully.');
            setEditShiftDialog({ open: false });
            await loadShifts();
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to update shift information.';
            setEditDialogError(msg);
        } finally {
            setEditDialogLoading(false);
        }
    };

    // ── Mobile shift card (used for upcoming / all / history) ───────────────
    const ShiftCard = ({ shift }: { shift: Shift }) => (
        <Card
            variant="outlined"
            sx={{ mb: 1.5, borderRadius: 2 }}
        >
            <CardActionArea sx={{ p: 0 }} onClick={() => handleOpenShiftDetails(shift)}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ flex: 1, mr: 1 }}>
                            {shift.clientName || 'Unknown Client'}
                        </Typography>
                        <Chip
                            label={shift.status}
                            color={getStatusColor(shift.status)}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    </Box>
                    <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                            <CalendarToday sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{formatDateShort(shift.startDateTime)}</Typography>
                            <Typography variant="caption" sx={{ mx: 0.25 }}>→</Typography>
                            <Typography variant="caption">{formatDateShort(shift.endDateTime)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                            <AccessTime sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{shift.durationHours}h &bull; {shift.shiftType}</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </CardActionArea>
            {canEditShiftInfo(shift) && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                    <Button
                        size="small"
                        startIcon={<Edit />}
                        variant="outlined"
                        onClick={() => handleOpenEditDialog(shift)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        Edit Shift Info
                    </Button>
                </Box>
            )}
        </Card>
    );

    // ── Mobile today-shift card (includes clock actions) ────────────────────
    const TodayShiftCard = ({ shift }: { shift: any }) => (
        <Card
            variant="outlined"
            sx={{
                mb: 1.5,
                borderRadius: 2,
                borderLeft: 4,
                borderLeftColor: shift.actualStart && !shift.actualEnd
                    ? 'warning.main'
                    : shift.actualEnd
                    ? 'success.main'
                    : 'primary.main',
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ flex: 1, mr: 1 }}>
                        {shift.clientName || 'Unknown Client'}
                    </Typography>
                    <Chip
                        label={shift.status}
                        color={getStatusColor(shift.status)}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                </Box>

                {/* Time info */}
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                        <Schedule sx={{ fontSize: 14 }} />
                        <Typography variant="caption">
                            {formatDateShort(shift.scheduledStart)} → {formatDateShort(shift.scheduledEnd)}
                        </Typography>
                    </Box>
                    {shift.actualStart && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'success.main' }}>
                            <Work sx={{ fontSize: 14 }} />
                            <Typography variant="caption">
                                Clocked in: {formatDateShort(shift.actualStart)}
                            </Typography>
                        </Box>
                    )}
                    {shift.actualEnd && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                            <WorkOff sx={{ fontSize: 14 }} />
                            <Typography variant="caption">
                                Clocked out: {formatDateShort(shift.actualEnd)}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                        <AccessTime sx={{ fontSize: 14 }} />
                        <Typography variant="caption">
                            {shift.actualHours ? `${shift.actualHours}h actual` : `${shift.scheduledHours}h scheduled`}
                        </Typography>
                    </Box>
                </Stack>

                {/* Clock actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant={canClockIn(shift) ? 'contained' : 'outlined'}
                        color="success"
                        size="small"
                        fullWidth
                        startIcon={<Work />}
                        disabled={!canClockIn(shift)}
                        onClick={() => handleClockAction('in', shift)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Clock In
                    </Button>
                    <Button
                        variant={canClockOut(shift) ? 'contained' : 'outlined'}
                        color="error"
                        size="small"
                        fullWidth
                        startIcon={<WorkOff />}
                        disabled={!canClockOut(shift)}
                        onClick={() => handleClockAction('out', shift)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Clock Out
                    </Button>
                </Box>

                {/* Tap for details hint */}
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
                >
                    {canEditShiftInfo(shift) && (
                        <Button
                            size="small"
                            startIcon={<Edit />}
                            variant="outlined"
                            onClick={() => handleOpenEditDialog(shift)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                        >
                            Edit Shift Info
                        </Button>
                    )}
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', ml: 'auto', cursor: 'pointer' }}
                        onClick={() => handleOpenShiftDetails(shift)}
                    >
                        <Typography variant="caption" color="primary">View details</Typography>
                        <ChevronRight sx={{ fontSize: 14, color: 'primary.main' }} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    // ── Desktop table for regular shifts ────────────────────────────────────
    const ShiftTable = ({ shifts, title }: { shifts: Shift[]; title: string }) => (
        <Box>
            <Typography variant="h6" gutterBottom>
                {title} ({shifts.length})
            </Typography>
            {shifts.length === 0 ? (
                <Alert severity="info">No shifts found</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Staff</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shifts.map((shift) => (
                                <TableRow
                                    key={shift.id}
                                    onClick={() => handleOpenShiftDetails(shift)}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                    <TableCell>{shift.id}</TableCell>
                                    <TableCell>{shift.clientName || 'Unknown Client'}</TableCell>
                                    <TableCell>{shift.staffName || 'Unknown Staff'}</TableCell>
                                    <TableCell>{formatDateTime(shift.startDateTime)}</TableCell>
                                    <TableCell>{formatDateTime(shift.endDateTime)}</TableCell>
                                    <TableCell>{shift.durationHours}h</TableCell>
                                    <TableCell>
                                        <Chip label={shift.status} color={getStatusColor(shift.status)} size="small" />
                                    </TableCell>
                                    <TableCell>{shift.shiftType}</TableCell>
                                    <TableCell>
                                        {canEditShiftInfo(shift) && (
                                            <Button
                                                size="small"
                                                startIcon={<Edit />}
                                                variant="outlined"
                                                onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(shift); }}
                                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                            >
                                                Edit Info
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    // ── Desktop table for today shifts ──────────────────────────────────────
    const TodayShiftsTable = ({ shifts }: { shifts: any[] }) => (
        <Box>
            <Typography variant="h6" gutterBottom>
                Today's Shifts ({shifts.length})
            </Typography>
            {shifts.length === 0 ? (
                <Alert severity="info">No shifts scheduled for today</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Shift ID</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Staff</TableCell>
                                <TableCell>Scheduled Start</TableCell>
                                <TableCell>Scheduled End</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                                <TableCell>Hours</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shifts.map((shift, index) => (
                                <TableRow
                                    key={shift.shiftId || index}
                                    onClick={() => handleOpenShiftDetails(shift)}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                    <TableCell>{shift.shiftId}</TableCell>
                                    <TableCell>{shift.clientName || 'Unknown Client'}</TableCell>
                                    <TableCell>{shift.staffName || 'Unknown Staff'}</TableCell>
                                    <TableCell>{formatDateTime(shift.scheduledStart)}</TableCell>
                                    <TableCell>{formatDateTime(shift.scheduledEnd)}</TableCell>
                                    <TableCell>
                                        <Chip label={shift.status} color={getStatusColor(shift.status)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={<Work />}
                                                disabled={!canClockIn(shift)}
                                                onClick={(e) => { e.stopPropagation(); handleClockAction('in', shift); }}
                                            >
                                                Clock In
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                startIcon={<WorkOff />}
                                                disabled={!canClockOut(shift)}
                                                onClick={(e) => { e.stopPropagation(); handleClockAction('out', shift); }}
                                            >
                                                Clock Out
                                            </Button>
                                            {canEditShiftInfo(shift) && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Edit />}
                                                    onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(shift); }}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Edit Info
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {shift.actualHours ? `${shift.actualHours}h` : `${shift.scheduledHours}h (scheduled)`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    // ── Shared list renderers (picks card vs table by screen size) ──────────
    const ShiftList = ({ shifts, title }: { shifts: Shift[]; title: string }) =>
        isMobile ? (
            <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                    {title} ({shifts.length})
                </Typography>
                {shifts.length === 0 ? (
                    <Alert severity="info">No shifts found</Alert>
                ) : (
                    shifts.map((shift) => <ShiftCard key={shift.id} shift={shift} />)
                )}
            </Box>
        ) : (
            <ShiftTable shifts={shifts} title={title} />
        );

    const TodayList = ({ shifts }: { shifts: any[] }) =>
        isMobile ? (
            <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                    Today's Shifts ({shifts.length})
                </Typography>
                {shifts.length === 0 ? (
                    <Alert severity="info">No shifts scheduled for today</Alert>
                ) : (
                    shifts.map((shift, i) => <TodayShiftCard key={shift.shiftId || i} shift={shift} />)
                )}
            </Box>
        ) : (
            <TodayShiftsTable shifts={shifts} />
        );

    return (
        <PageLayout>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* ── Header ───────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                        My Shifts
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
                        onClick={loadShifts}
                        disabled={loading}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* ── Staff profile card ───────────────────────────────── */}
                {currentStaffProfile ? (
                    <Card
                        variant="outlined"
                        sx={{ mb: 2.5, borderRadius: 2, background: 'linear-gradient(135deg, #1976d210 0%, #42a5f510 100%)' }}
                    >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontSize: '1.1rem' }}>
                                    {currentStaffProfile.firstName?.[0]}{currentStaffProfile.lastName?.[0]}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                                        {currentStaffProfile.firstName} {currentStaffProfile.lastName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                        <Chip label={currentStaffProfile.position} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                        <Chip label={currentStaffProfile.department} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                        {/* <Chip label={`ID: ${currentStaffProfile.employeeId}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} /> */}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">Loading profile…</Typography>
                    </Box>
                )}

                {/* ── Alerts ───────────────────────────────────────────── */}
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                {/* ── Tabs ─────────────────────────────────────────────── */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={currentTab}
                        onChange={(_, v) => setCurrentTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <Tab label="Today" sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 70, sm: 'auto' } }} />
                        <Tab label="Upcoming" sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 80, sm: 'auto' } }} />
                        <Tab label="All" sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 50, sm: 'auto' } }} />
                        <Tab label="History" sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 70, sm: 'auto' } }} />
                    </Tabs>
                </Box>

                {/* ── Tab panels ───────────────────────────────────────── */}
                <TabPanel value={currentTab} index={0}>
                    <TodayList shifts={todayShifts} />
                </TabPanel>

                <TabPanel value={currentTab} index={1}>
                    <ShiftList shifts={upcomingShifts} title="Upcoming (Next 7 Days)" />
                </TabPanel>

                <TabPanel value={currentTab} index={2}>
                    <ShiftList shifts={allShifts} title="All Shifts" />
                    {allShifts.length >= limit && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={10}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        </Box>
                    )}
                </TabPanel>

                <TabPanel value={currentTab} index={3}>
                    <ShiftList shifts={historyShifts} title="History (Last 30 Days)" />
                </TabPanel>
            </Box>

            {/* ── Clock In/Out Dialog ──────────────────────────────────── */}
            <Dialog
                open={clockDialog.open}
                onClose={() => setClockDialog({ open: false, type: 'in' })}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {clockDialog.type === 'in' ? (
                            <Work color="success" />
                        ) : (
                            <WorkOff color="error" />
                        )}
                        {clockDialog.type === 'in' ? 'Clock In' : 'Clock Out'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {clockDialogError && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2, borderRadius: 2 }}
                            onClose={() => setClockDialogError(null)}
                        >
                            {clockDialogError}
                        </Alert>
                    )}
                    {clockDialog.shift && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Shift #{clockDialog.shift.shiftId}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                                {clockDialog.shift.clientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDateTime(clockDialog.shift.scheduledStart)} – {formatDateTime(clockDialog.shift.scheduledEnd)}
                            </Typography>
                        </Box>
                    )}
                    <Alert severity="info" icon={false} sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            📍 Your location will be recorded
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                            {clockDialog.type === 'in'
                                ? 'Only clock in once you are at the client and ready to start the shift. Clocking in early or off-site can affect payroll and may be flagged for review.'
                                : 'Only clock out when the shift is actually finished. Your location at this moment will be saved alongside the time.'}
                        </Typography>
                    </Alert>
                    {clockDialog.type === 'out' && (
                        <TextField
                            margin="dense"
                            label="Travel kilometres (optional)"
                            type="number"
                            fullWidth
                            value={travelKm}
                            onChange={(e) => setTravelKm(e.target.value)}
                            placeholder="e.g. 12.5"
                            inputProps={{ min: 0, step: 0.1 }}
                            helperText="Reimbursed per the configured Transport rate."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    )}
                    {clockDialog.type === 'out' && (
                        <TextField
                            margin="dense"
                            label="Work Information (optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={clockOutWorkInfo}
                            onChange={(e) => setClockOutWorkInfo(e.target.value)}
                            placeholder="Describe the support provided (e.g. personal care, meal prep, community access)..."
                            helperText="Separate from notes — describes the tasks performed. Editable within 7 days of clock-out."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Notes (optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes…"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setClockDialog({ open: false, type: 'in' })}
                        variant="outlined"
                        fullWidth
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={performClockAction}
                        variant="contained"
                        color={clockDialog.type === 'in' ? 'success' : 'error'}
                        fullWidth
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {clockDialog.type === 'in' ? 'Clock In' : 'Clock Out'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Shift Info Dialog ──────────────────────────────── */}
            <Dialog
                open={editShiftDialog.open}
                onClose={() => setEditShiftDialog({ open: false })}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Edit color="primary" />
                        Edit Shift Information
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {editDialogError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setEditDialogError(null)}>
                            {editDialogError}
                        </Alert>
                    )}
                    {editShiftDialog.shift && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                                Shift #{editShiftDialog.shift.id ?? editShiftDialog.shift.shiftId}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                                {editShiftDialog.shift.clientName}
                            </Typography>
                        </Box>
                    )}
                    <Alert severity="info" icon={false} sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                            You can update shift information and travel kilometres within 7 days of clocking out.
                        </Typography>
                    </Alert>
                    <TextField
                        margin="dense"
                        label="Work Information"
                        fullWidth
                        multiline
                        rows={4}
                        value={editWorkInfo}
                        onChange={(e) => setEditWorkInfo(e.target.value)}
                        placeholder="Describe the support provided (e.g. personal care, meal prep, community access activities)..."
                        helperText="Describes the tasks performed during this shift."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                        margin="dense"
                        label="Travel kilometres"
                        type="number"
                        fullWidth
                        value={editTravelKm}
                        onChange={(e) => setEditTravelKm(e.target.value)}
                        placeholder="e.g. 12.5"
                        inputProps={{ min: 0, step: 0.1 }}
                        helperText="Leave blank to keep current value."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setEditShiftDialog({ open: false })}
                        variant="outlined"
                        fullWidth
                        disabled={editDialogLoading}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={performEditShiftInfo}
                        variant="contained"
                        fullWidth
                        disabled={editDialogLoading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {editDialogLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Shift Details Modal ──────────────────────────────────── */}
            <ShiftDetailsModal
                open={shiftDetailsModalOpen}
                onClose={handleCloseShiftDetails}
                shift={selectedShiftDetails}
                hideFinancials
                onEditShiftInfo={(shift) => {
                    handleCloseShiftDetails();
                    handleOpenEditDialog(shift);
                }}
            />
        </PageLayout>
    );
};

export default UserShiftsPage;

